import { CheckIcon } from '@phosphor-icons/react';
import { RepeatIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { VideoProgressBar } from '@/components/VideoPlayer/VideoProgressBar';
import { useVideoPlayerContext } from '@/components/VideoPlayer/VideoPlayerProvider';
import { usePlaybackRate } from '@/components/VideoPlayer/hooks/usePlaybackRate';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItemBase,
    DropdownMenuLabel,
    DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@/components/ui/Tooltip';
import { Button } from '@/components/ui/Button';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { cn } from '@/lib/utils';
import { PLAYBACK_SPEEDS } from '@/utils/video-player';

export function PipPlayerBottomBar() {
    const { t } = useTranslation(I18nNamespace.VIDEO);
    const { loop, setLoop, streaming } = useVideoPlayerContext();
    const { playbackRate, setRate } = usePlaybackRate();

    const toggleLoop = () => {
        setLoop(!loop);
    };

    return (
        <div
            className="absolute inset-x-0 bottom-0 z-[2] flex h-[66px] flex-col items-stretch gap-2 bg-[linear-gradient(179.82deg,rgba(17,18,20,0)_0.15%,#111214_83.43%)] px-3 py-2 opacity-0 transition-opacity duration-200 group-hover/pip:opacity-100"
            onClick={(e) => e.stopPropagation()}
        >
            <div className="relative z-[3] flex h-5 items-center gap-4">
                <div className="flex h-5 flex-1 items-center gap-4">
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Button
                                type="button"
                                variant="ghost"
                                shape="none"
                                onClick={toggleLoop}
                                disabled={streaming.live}
                                className={cn(
                                    'h-[18px] w-5 p-0 bg-transparent text-[#9C9C9C] hover:bg-transparent hover:text-white-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40 disabled:opacity-50',
                                    loop &&
                                        'text-danger-200 hover:text-danger-200',
                                )}
                                aria-label={
                                    loop
                                        ? t('controls.loopOn')
                                        : t('controls.loopOff')
                                }
                            >
                                <RepeatIcon className="size-5" />
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent side="top" sideOffset={8}>
                            {loop
                                ? t('controls.loopOn')
                                : t('controls.loopOff')}
                        </TooltipContent>
                    </Tooltip>
                </div>

                <div className="flex h-5 flex-1 items-center justify-end gap-4">
                    {!streaming.live && (
                        <DropdownMenu modal={false}>
                            <DropdownMenuTrigger asChild>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    shape="none"
                                    className="h-[18px] p-0 bg-transparent text-xs font-medium leading-[18px] text-[#9C9C9C] hover:bg-transparent hover:text-white-100"
                                >
                                    {t('controls.playbackSpeed', {
                                        rate: playbackRate,
                                    })}
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                                variant="compact"
                                align="end"
                                side="top"
                                className="z-[80]"
                            >
                                <DropdownMenuLabel>
                                    {t('controls.playbackSpeedLabel')}
                                </DropdownMenuLabel>
                                {PLAYBACK_SPEEDS.map((speed) => (
                                    <DropdownMenuItemBase
                                        key={speed}
                                        onClick={() => setRate(speed)}
                                        className={cn(
                                            'justify-between text-xs text-white-100 py-1 hover:!bg-transparent focus:!bg-transparent',
                                            playbackRate === speed &&
                                                'bg-grey-500 hover:!bg-grey-500 focus:!bg-grey-500',
                                        )}
                                    >
                                        {speed === 1
                                            ? `1x - ${t('controls.normal')}`
                                            : `${speed}x`}
                                        {playbackRate === speed && (
                                            <CheckIcon className="size-4 text-grey-200 flex-shrink-0" />
                                        )}
                                    </DropdownMenuItemBase>
                                ))}
                            </DropdownMenuContent>
                        </DropdownMenu>
                    )}
                </div>
            </div>
            <VideoProgressBar overlay={false} variant="pip" visible={true} />
        </div>
    );
}
